import { Client, type IMessage, type StompSubscription } from '@stomp/stompjs';
import { create } from 'zustand';
import { useEffect, useRef } from 'react';
import { getAccessToken } from '@/api/client';

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected';

interface RealtimeState {
  status: ConnectionStatus;
  setStatus: (status: ConnectionStatus) => void;
}

export const useRealtimeStore = create<RealtimeState>((set) => ({
  status: 'disconnected',
  setStatus: (status) => set({ status }),
}));

type Handler = (payload: unknown, message: IMessage) => void;

/**
 * One STOMP connection shared by the whole application.
 *
 * Components register interest in a destination through {@link useTopic}; this module
 * keeps a single socket and multiplexes subscriptions over it, so opening five screens
 * does not open five sockets. Reconnection is automatic and re-subscribes everything that
 * was active when the connection dropped.
 */
class RealtimeConnection {
  private client: Client | null = null;
  private readonly handlers = new Map<string, Set<Handler>>();
  private readonly subscriptions = new Map<string, StompSubscription>();

  connect() {
    if (this.client?.active) return;

    const token = getAccessToken();
    if (!token) return;

    useRealtimeStore.getState().setStatus('connecting');

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    this.client = new Client({
      brokerURL: `${protocol}//${window.location.host}/ws`,
      // the server authenticates the CONNECT frame, so the token never enters the URL
      connectHeaders: { Authorization: `Bearer ${token}` },
      reconnectDelay: 5000,
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      onConnect: () => {
        useRealtimeStore.getState().setStatus('connected');
        // restore every destination that was live before the drop
        this.handlers.forEach((_, destination) => this.subscribe(destination));
      },
      onDisconnect: () => useRealtimeStore.getState().setStatus('disconnected'),
      onWebSocketClose: () => useRealtimeStore.getState().setStatus('disconnected'),
      onStompError: (frame) => {
        console.error('STOMP error', frame.headers.message, frame.body);
      },
    });

    this.client.activate();
  }

  disconnect() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.subscriptions.clear();
    this.handlers.clear();
    void this.client?.deactivate();
    this.client = null;
    useRealtimeStore.getState().setStatus('disconnected');
  }

  addHandler(destination: string, handler: Handler) {
    let handlers = this.handlers.get(destination);
    if (!handlers) {
      handlers = new Set();
      this.handlers.set(destination, handlers);
    }
    handlers.add(handler);

    if (this.client?.connected && !this.subscriptions.has(destination)) {
      this.subscribe(destination);
    }
  }

  removeHandler(destination: string, handler: Handler) {
    const handlers = this.handlers.get(destination);
    if (!handlers) return;
    handlers.delete(handler);

    // the last listener for a destination takes the subscription down with it
    if (handlers.size === 0) {
      this.handlers.delete(destination);
      this.subscriptions.get(destination)?.unsubscribe();
      this.subscriptions.delete(destination);
    }
  }

  private subscribe(destination: string) {
    if (!this.client?.connected || this.subscriptions.has(destination)) return;
    const subscription = this.client.subscribe(destination, (message) => {
      let payload: unknown = message.body;
      try {
        payload = JSON.parse(message.body);
      } catch {
        // a non-JSON frame is passed through untouched
      }
      this.handlers.get(destination)?.forEach((handler) => handler(payload, message));
    });
    this.subscriptions.set(destination, subscription);
  }
}

export const realtime = new RealtimeConnection();

/**
 * Subscribes to a STOMP destination for the lifetime of the component.
 *
 * @param destination e.g. `/topic/orders`, or null to subscribe to nothing
 */
export const useTopic = (destination: string | null, handler: Handler) => {
  // the handler is kept in a ref so an inline arrow function does not
  // re-subscribe on every render
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!destination) return;
    const stable: Handler = (payload, message) => handlerRef.current(payload, message);
    realtime.addHandler(destination, stable);
    return () => realtime.removeHandler(destination, stable);
  }, [destination]);
};
